import { useEffect, useState } from 'react';

/**
 * Two routers, deliberately, because there are two products in one bundle.
 *
 * The public website uses real paths — `/`, `/pricing`, `/demo`, `/services`,
 * `/login`. A marketing page that lives at `/#pricing` cannot be linked to
 * from an email, shared in a slide, or given its own title and description,
 * and those are most of what a marketing page is for.
 *
 * The application keeps the hash sections it has always had, under `/app`.
 * They were not moved to paths: `navigate()` is called from every page, the
 * browser suites drive `#section` URLs, and the demonstration this was ported
 * from used the same anchors. Changing all of that to gain tidier URLs behind
 * a login is a large diff for no user-visible benefit, and the brief asks for
 * a refinement rather than a migration.
 *
 * So: `/app#breakdown?doc=…` — path chooses the product, hash chooses the
 * section within it. Every existing hash URL still means what it meant.
 */

/**
 * `funding` is deliberately absent.
 *
 * Funding Radar is built — the page, the routes, the curation CLI and the
 * scoring are all still here — and it has no curated board of opportunities
 * behind it, so it is not shown to anyone until it does. Taking it out of this
 * list is what makes it unreachable rather than merely unlinked: `parse()`
 * falls back to the dashboard for a section it does not recognise, so
 * /app#funding lands somewhere real instead of on an empty board.
 *
 * Restoring it is this line, the sidebar entry in Shell.tsx, and the route in
 * App.tsx. Nothing else was deleted.
 */
export const SECTIONS = [
  'dashboard', 'source', 'breakdown', 'ask', 'explain',
  'translate', 'admin', 'plan', 'services',
] as const;

export type Section = (typeof SECTIONS)[number];

export const APP_BASE = '/app';

export type PublicRoute =
  | 'home' | 'pricing' | 'demo' | 'services'
  | 'security' | 'resources' | 'about'
  | 'contact' | 'login';

/** The public site's routes. Anything unrecognised falls back to the home page. */
const PUBLIC_PATHS: Record<string, PublicRoute> = {
  '/': 'home',
  '/pricing': 'pricing',
  '/demo': 'demo',
  '/services': 'services',
  '/security': 'security',
  '/resources': 'resources',
  '/about': 'about',
  '/contact': 'contact',
  '/login': 'login',
};

export const PUBLIC_PATH_FOR: Record<PublicRoute, string> = {
  home: '/',
  pricing: '/pricing',
  demo: '/demo',
  services: '/services',
  security: '/security',
  resources: '/resources',
  about: '/about',
  contact: '/contact',
  login: '/login',
};

export interface Location {
  area: 'public' | 'app';
  /** Meaningful when area is 'public'. */
  route: PublicRoute;
  /** Meaningful when area is 'app'. */
  section: Section;
  params: URLSearchParams;
}

/** A pathname with any trailing slash removed, so `/pricing/` is `/pricing`. */
const tidy = (pathname: string): string => {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
};

export const isAppPath = (pathname: string): boolean => {
  const path = tidy(pathname);
  return path === APP_BASE || path.startsWith(`${APP_BASE}/`);
};

const parse = (): Location => {
  const raw = window.location.hash.replace(/^#/, '');
  const [name, query] = raw.split('?');
  const params = new URLSearchParams(query ?? '');
  const section = SECTIONS.includes(name as Section) ? (name as Section) : 'dashboard';
  const path = tidy(window.location.pathname);

  if (isAppPath(path)) return { area: 'app', route: 'home', section, params };
  return { area: 'public', route: PUBLIC_PATHS[path] ?? 'home', section, params };
};

/**
 * pushState does not fire popstate — that event is for the back button only —
 * so an in-page navigation has to announce itself. One custom event, listened
 * for alongside popstate and hashchange.
 */
const ROUTE_EVENT = 'inbrief:navigated';

function announce() {
  window.dispatchEvent(new Event(ROUTE_EVENT));
}

export function useRoute(): Location {
  const [location, setLocation] = useState(parse);

  useEffect(() => {
    const onChange = () => setLocation(parse());
    window.addEventListener('hashchange', onChange);
    window.addEventListener('popstate', onChange);
    window.addEventListener(ROUTE_EVENT, onChange);
    return () => {
      window.removeEventListener('hashchange', onChange);
      window.removeEventListener('popstate', onChange);
      window.removeEventListener(ROUTE_EVENT, onChange);
    };
  }, []);

  return location;
}

/**
 * Moves to a path on the public site, or to `/app`.
 *
 * Scrolls to the top, unless the path names a section — in which case
 * `useHashTarget` below scrolls there once the new page has rendered. This
 * used to scroll to the top unconditionally, which meant a link to
 * `/#how-it-works` from any other page put you at the top of the home page
 * with the right URL in the bar and no indication of what had gone wrong.
 */
export function go(path: string) {
  const [pathname = '/', hash] = path.split('#');
  if (tidy(pathname) === tidy(window.location.pathname) && !hash) {
    window.scrollTo({ top: 0, behavior: 'auto' });
    return;
  }
  window.history.pushState({}, '', path);
  if (!hash) window.scrollTo({ top: 0, behavior: 'auto' });
  announce();
}

/**
 * Scrolls to the section a public URL names.
 *
 * Three things arrive at a section by URL rather than by clicking a link on
 * the page it lives on, and none of them worked: a nav anchor followed from
 * another page, a link somebody shared, and a bookmark. All three landed at
 * the top of the home page.
 *
 * The browser's own anchor handling does not survive here. The section exists
 * in the prerendered HTML, but hydration re-renders the page underneath the
 * browser's scroll, and the position is lost. So the scroll is done after
 * render, and retried for a few frames because the target may still be
 * mounting — a link into a section is worth a dozen frames of patience.
 *
 * `behavior: 'auto'` rather than smooth: arriving at a page you asked for by
 * URL should not begin with a journey from somewhere you never were.
 */
export function useHashTarget(area: 'public' | 'app', route: PublicRoute) {
  useEffect(() => {
    if (area !== 'public') return;
    const id = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    if (!id) return;

    let frame = 0;
    let handle = 0;
    const attempt = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
      if (frame++ < 30) handle = requestAnimationFrame(attempt);
    };
    handle = requestAnimationFrame(attempt);
    return () => cancelAnimationFrame(handle);
  }, [area, route]);
}

/**
 * Moves to a section of the application.
 *
 * Unchanged in signature and meaning, so every existing call site still works.
 * It now also makes sure the path is `/app` first — a signed-in user following
 * a link from the public site would otherwise set a hash on `/pricing` and see
 * the pricing page with a section anchor that means nothing there.
 */
export function navigate(section: Section, params?: Record<string, string>) {
  const query = params && Object.keys(params).length
    ? '?' + new URLSearchParams(params).toString()
    : '';
  if (!isAppPath(window.location.pathname)) {
    window.history.pushState({}, '', `${APP_BASE}#${section}${query}`);
    announce();
    return;
  }
  window.location.hash = `${section}${query}`;
}
